/**
 * Formulaire de création/édition de patient
 */

import { useState, FormEvent } from 'react';
import { PatientCreate } from '@/shared/types/patient';
import { patientService } from '@/shared/services/patientService';

interface PatientFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export function PatientForm({ onSuccess, onCancel }: PatientFormProps) {
  const [formData, setFormData] = useState<PatientCreate>({
    nom: '',
    prenom: '',
    date_naissance: undefined,
    ipp: '',
    service_actuel: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await patientService.create(formData);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de création');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="card card-premium bounce-in">
      <div className="card-header bg-gradient-primary text-white">
        <i className="bi bi-person-plus-fill me-2"></i>
        <strong>Nouveau patient</strong>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-danger d-flex align-items-center mb-4 fade-in" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>{error}</div>
            </div>
          )}

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-person me-1"></i>
                Nom *
              </label>
              <input
                type="text"
                required
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                className="form-control"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-person me-1"></i>
                Prénom *
              </label>
              <input
                type="text"
                required
                value={formData.prenom}
                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                className="form-control"
              />
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-hash me-1"></i>
                IPP *
              </label>
              <input
                type="text"
                required
                value={formData.ipp}
                onChange={(e) => setFormData({ ...formData, ipp: e.target.value })}
                placeholder="IPP2024XXX"
                className="form-control"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">
                <i className="bi bi-calendar-event me-1"></i>
                Date de naissance
              </label>
              <input
                type="date"
                value={formData.date_naissance || ''}
                onChange={(e) => setFormData({ ...formData, date_naissance: e.target.value || undefined })}
                className="form-control"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label fw-semibold">
              <i className="bi bi-hospital me-1"></i>
              Service actuel
            </label>
            <select
              value={formData.service_actuel}
              onChange={(e) => setFormData({ ...formData, service_actuel: e.target.value })}
              className="form-select"
            >
              <option value="">Sélectionner un service</option>
              <option value="Cardiologie">Cardiologie</option>
              <option value="Neurologie">Neurologie</option>
              <option value="Pneumologie">Pneumologie</option>
              <option value="Urgences">Urgences</option>
              <option value="Réanimation">Réanimation</option>
            </select>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <button type="button" onClick={onCancel} className="btn btn-outline-secondary">
              <i className="bi bi-x-circle me-2"></i>
              Annuler
            </button>
            <button type="submit" disabled={isLoading} className="btn btn-primary shadow-glow">
              <i className={`bi ${isLoading ? 'bi-hourglass-split' : 'bi-check-circle'} me-2`}></i>
              {isLoading ? 'Création...' : 'Créer le patient'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
